import {
  AccessGatewayError,
  LOCK_DURATION_MS,
  MAX_PIN_FAILURES,
  isPinLocked,
  recordPinFailure,
  recordPinSuccess,
  type AccessGateway,
  type AccessRequest,
  type MemberRole,
  type MemberSessionView,
  type MemberStatus,
  type SessionView,
} from '../../domain/access'
import { normalizeMemberName, parseDisplayName, parsePin, parseTripCode } from '../../domain/validation'
import { CARAVAN_TRIP } from '../../config/trip'

const STORAGE_KEY = 'caravan-demo-access-v2'
const LEGACY_STORAGE_KEY = 'caravan-demo-access-v1'
const ADMIN_PIN_HASH = '4c6fb73b69fbdabd72d9ec5d260f1aaa151fabd9e5b67cc4912600d3af322025'

type DemoMember = {
  id: string
  displayName: string
  normalizedName: string
  role: MemberRole
  status: MemberStatus
  pinHash: string | null
  failedAttempts: number
  lockedUntil: number | null
}

type DemoSession = MemberSessionView

type DemoState = {
  members: DemoMember[]
  sessions: DemoSession[]
}

type DemoGatewayOptions = {
  storage?: Storage
  now?: () => number
}

const seedMembers: DemoMember[] = CARAVAN_TRIP.participants.map((participant) => ({
  id: participant.id,
  displayName: participant.displayName,
  normalizedName: normalizeMemberName(participant.displayName),
  role: participant.role,
  status: participant.role === 'admin' ? 'active' : 'invited',
  pinHash: participant.role === 'admin' ? ADMIN_PIN_HASH : null,
  failedAttempts: 0,
  lockedUntil: null,
}))

function newSeedState(): DemoState {
  return { members: structuredClone(seedMembers), sessions: [] }
}

function readState(storage: Storage): DemoState {
  const raw = storage.getItem(STORAGE_KEY) ?? storage.getItem(LEGACY_STORAGE_KEY)
  if (!raw) return newSeedState()
  try {
    const parsed = JSON.parse(raw) as DemoState
    if (parsed.members.length !== 6 || !Array.isArray(parsed.sessions)) return newSeedState()
    const members = seedMembers.map((seed) => {
      const previous = parsed.members.find((member) => member.id === seed.id)
      return previous ? {
        ...seed,
        status: previous.status,
        pinHash: previous.pinHash,
        failedAttempts: previous.failedAttempts,
        lockedUntil: previous.lockedUntil,
      } : seed
    })
    const displayNames = new Map(members.map((member) => [member.id, member.displayName]))
    const state = {
      members,
      sessions: parsed.sessions.map((session) => ({
        ...session,
        displayName: displayNames.get(session.memberId) ?? session.displayName,
      })),
    }
    storage.setItem(STORAGE_KEY, JSON.stringify(state))
    const legacyAdminSession = storage.getItem(`${LEGACY_STORAGE_KEY}:active-admin-session`)
    if (legacyAdminSession && !storage.getItem(`${STORAGE_KEY}:active-admin-session`)) {
      storage.setItem(`${STORAGE_KEY}:active-admin-session`, legacyAdminSession)
    }
    return state
  } catch {
    return newSeedState()
  }
}

async function hashPin(pin: string): Promise<string> {
  const bytes = new TextEncoder().encode(`caravan-demo:${pin}`)
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

function createId(): string {
  return globalThis.crypto.randomUUID()
}

export function createDemoAccessGateway(options: DemoGatewayOptions = {}): AccessGateway {
  const storage = options.storage ?? window.localStorage
  const now = options.now ?? Date.now

  function persist(state: DemoState) {
    storage.setItem(STORAGE_KEY, JSON.stringify(state))
  }

  function requireAdmin(state: DemoState): DemoMember {
    const activeSessionId = storage.getItem(`${STORAGE_KEY}:active-admin-session`)
    const session = state.sessions.find(
      (candidate) => candidate.id === activeSessionId && candidate.revokedAt === null,
    )
    const member = state.members.find((candidate) => candidate.id === session?.memberId)
    if (member?.role !== 'admin') {
      throw new AccessGatewayError('FORBIDDEN', 'Droits administrateur requis.')
    }
    return member
  }

  async function access(request: AccessRequest): Promise<SessionView> {
    const state = readState(storage)
    if (request.action === 'refresh') {
      if (!request.sessionId) throw new AccessGatewayError('REVOKED', 'Session introuvable.')
      return refresh(request.sessionId)
    }
    if (request.action === 'activate_admin') {
      throw new AccessGatewayError(
        'FORBIDDEN',
        'L’activation administrateur est provisionnée hors bande.',
      )
    }

    const tripCode = parseTripCode(request.tripCode)
    const displayName = parseDisplayName(request.displayName)
    const normalizedName = normalizeMemberName(displayName)
    const pin = parsePin(request.pin)
    const member = state.members.find((candidate) => candidate.normalizedName === normalizedName)
    const currentTime = now()

    if (tripCode.toLocaleUpperCase('fr-FR') !== 'CARAVAN' || !member || member.status === 'archived') {
      throw new AccessGatewayError('INVALID_CREDENTIALS', 'Accès impossible. Vérifiez vos informations.')
    }
    if (isPinLocked(member, currentTime)) {
      throw new AccessGatewayError('LOCKED', 'Trop de tentatives. Réessayez plus tard.', {
        retryAfterMs: Math.max(0, (member.lockedUntil ?? currentTime) - currentTime),
      })
    }

    if (request.action === 'join') {
      if (member.status !== 'invited' || member.pinHash !== null) {
        throw new AccessGatewayError('INVALID_CREDENTIALS', 'Accès impossible. Vérifiez vos informations.')
      }
      member.pinHash = await hashPin(pin)
      member.status = 'active'
      Object.assign(member, recordPinSuccess(member))
    } else {
      const candidateHash = await hashPin(pin)
      if (member.pinHash === null || candidateHash !== member.pinHash) {
        Object.assign(member, recordPinFailure(member, currentTime))
        if (member.failedAttempts >= MAX_PIN_FAILURES) member.status = 'locked'
        persist(state)
        if (member.failedAttempts >= MAX_PIN_FAILURES) {
          throw new AccessGatewayError('LOCKED', 'Trop de tentatives. Réessayez dans 15 minutes.', {
            retryAfterMs: LOCK_DURATION_MS,
          })
        }
        throw new AccessGatewayError('INVALID_CREDENTIALS', 'Accès impossible. Vérifiez vos informations.')
      }
      Object.assign(member, recordPinSuccess(member))
      member.status = 'active'
    }

    const instant = new Date(currentTime).toISOString()
    const session: DemoSession = {
      id: createId(),
      memberId: member.id,
      displayName: member.displayName,
      deviceLabel: request.deviceLabel?.slice(0, 60) ?? null,
      createdAt: instant,
      lastSeenAt: instant,
      revokedAt: null,
    }
    state.sessions.push(session)
    if (member.role === 'admin') storage.setItem(`${STORAGE_KEY}:active-admin-session`, session.id)
    persist(state)
    return toSessionView(member, session)
  }

  function refresh(sessionId: string): Promise<SessionView> {
    return Promise.resolve().then(() => {
      const state = readState(storage)
      const session = state.sessions.find((candidate) => candidate.id === sessionId)
      const member = state.members.find((candidate) => candidate.id === session?.memberId)
      if (session?.revokedAt !== null || !member || member.status === 'archived') {
        throw new AccessGatewayError('REVOKED', 'Cette session a été révoquée.')
      }
      session.lastSeenAt = new Date(now()).toISOString()
      persist(state)
      return toSessionView(member, session)
    })
  }

  function toSessionView(member: DemoMember, session: DemoSession): SessionView {
    return {
      sessionId: session.id,
      tripId: CARAVAN_TRIP.id,
      memberId: member.id,
      displayName: member.displayName,
      role: member.role,
      status: 'active',
    }
  }

  return {
    mode: 'demo',
    selectMember(memberId, deviceLabel) {
      const state = readState(storage)
      const member = state.members.find((candidate) => candidate.id === memberId)
      if (!member || member.status === 'archived') {
        throw new AccessGatewayError('INVALID_CREDENTIALS', 'Membre introuvable.')
      }
      member.status = 'active'
      Object.assign(member, recordPinSuccess(member))
      const instant = new Date(now()).toISOString()
      const session: DemoSession = {
        id: createId(),
        memberId: member.id,
        displayName: member.displayName,
        deviceLabel: deviceLabel?.slice(0, 60) ?? null,
        createdAt: instant,
        lastSeenAt: instant,
        revokedAt: null,
      }
      state.sessions.push(session)
      if (member.role === 'admin') storage.setItem(`${STORAGE_KEY}:active-admin-session`, session.id)
      persist(state)
      return Promise.resolve(toSessionView(member, session))
    },
    access,
    refresh,
    listMembers() {
      const state = readState(storage)
      requireAdmin(state)
      return Promise.resolve(
        state.members.map(({ id, displayName, role, status }) => ({
          id,
          displayName,
          role,
          status,
        })),
      )
    },
    listSessions() {
      const state = readState(storage)
      requireAdmin(state)
      return Promise.resolve(state.sessions.map((session) => ({ ...session })))
    },
    async resetMemberPin(memberId, pin) {
      const state = readState(storage)
      requireAdmin(state)
      const member = state.members.find((candidate) => candidate.id === memberId)
      if (!member) throw new AccessGatewayError('INVALID_CREDENTIALS', 'Membre introuvable.')
      member.pinHash = await hashPin(parsePin(pin))
      member.status = 'active'
      Object.assign(member, recordPinSuccess(member))
      persist(state)
    },
    revokeSession(sessionId) {
      const state = readState(storage)
      requireAdmin(state)
      const session = state.sessions.find((candidate) => candidate.id === sessionId)
      if (session?.revokedAt === null) session.revokedAt = new Date(now()).toISOString()
      persist(state)
      return Promise.resolve()
    },
    archiveMember(memberId) {
      const state = readState(storage)
      const admin = requireAdmin(state)
      if (memberId === admin.id) {
        throw new AccessGatewayError('FORBIDDEN', 'L’administrateur initial ne peut pas être archivé.')
      }
      const member = state.members.find((candidate) => candidate.id === memberId)
      if (member) member.status = 'archived'
      for (const session of state.sessions.filter((candidate) => candidate.memberId === memberId)) {
        session.revokedAt ??= new Date(now()).toISOString()
      }
      persist(state)
      return Promise.resolve()
    },
  }
}
